/**
 * The still a moving picture owes (D182 for sprites, D263 for plates;
 * content-standards §3): which file a picture draws depends on whether the
 * viewer asked for reduced motion.
 *
 * @remarks
 * The rest of the shell's reduced-motion handling is CSS — a transition
 * that becomes a cut — and needs no script. A picture is different: its
 * motion is IN the file (an animated WebP plays itself), so honoring the
 * preference means choosing a different file, and the choice has to be
 * made wherever the picture's source is read — the live layer, the lightbox
 * copy, and the D99 decode list that gates the dissolve. One reader of the
 * preference and one chooser of the source, so the three cannot disagree.
 *
 * The preference is read as an external store rather than once at mount:
 * a viewer who switches it on mid-session gets the still on the next
 * render, the way a viewer who switches it on gets their transitions cut.
 *
 * @packageDocumentation
 */

import { useSyncExternalStore } from 'react';

/** The one query this module asks. */
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * The media query list, or nothing where the environment has no media
 * queries (jsdom ships no `matchMedia`) — there, nobody has asked for
 * reduced motion.
 */
const reducedMotionQueryOf = (): MediaQueryList | undefined =>
	typeof window.matchMedia === 'function' ? window.matchMedia(REDUCED_MOTION_QUERY) : undefined;

const subscribe = (onChange: () => void): (() => void) => {
	const query = reducedMotionQueryOf();

	query?.addEventListener('change', onChange);

	return () => {
		query?.removeEventListener('change', onChange);
	};
};

const getSnapshot = (): boolean => reducedMotionQueryOf()?.matches ?? false;

/** Whether the viewer has asked the platform for reduced motion — live, not a mount-time read. */
export const usePrefersReducedMotion = (): boolean => useSyncExternalStore(subscribe, getSnapshot);

/** A picture as far as the choice cares: the moving file, and the still when the pack declared one (D182 sprites, D263 plates). */
export interface MotionSources {
	readonly reducedMotionSrc?: string;
	readonly src: string;
}

/** A sprite as far as the choice cares: its two files, and (D255) its play-once arrival. */
export interface SpriteSources extends MotionSources {
	readonly transition?: { readonly src: string };
}

/**
 * The file a picture draws for this viewer, before any arrival: the still
 * when the viewer asked for reduced motion and the pack provided one.
 *
 * @param sources - The picture's files
 * @param isReducedMotionPreferred - The viewer's preference
 * @returns The still, or the picture as authored
 */
export const motionSourceOf = ({ reducedMotionSrc, src }: MotionSources, isReducedMotionPreferred: boolean): string =>
	isReducedMotionPreferred ? (reducedMotionSrc ?? src) : src;

/**
 * The file a sprite draws for this viewer.
 *
 * @remarks
 * D255: a sprite the player just watched arrive draws its transition — unless
 * the viewer asked for reduced motion, who sees the sprite at rest at once.
 *
 * @param sprite - The sprite's sources
 * @param isReducedMotionPreferred - The viewer's preference
 * @param isArriving - Whether the player just watched it replace the sprite its transition plays from
 * @returns The still when the viewer asked for it and the pack provided one; the transition while it arrives; otherwise the sprite as authored
 */
export const spriteSourceOf = (sprite: SpriteSources, isReducedMotionPreferred: boolean, isArriving: boolean): string =>
	!isReducedMotionPreferred && isArriving && sprite.transition !== undefined ? sprite.transition.src : motionSourceOf(sprite, isReducedMotionPreferred);
